/**
 * Public SOC 2 Type II Readiness page.
 *
 * Buyers' procurement teams ask "are you SOC 2?". The honest answer is "Type II
 * audit targeted September 30, 2026, here is our live control matrix and 100% readiness".
 * This page is that honest answer.
 */
import React, { useEffect, useState } from 'react';
import SEOHead from '../components/SEOHead';
import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ShieldCheck, CircleDot, Clock, ExternalLink, FileCheck, Lock, Activity, Server, ArrowRight } from 'lucide-react';
// v34.55 — promoted from BackToHomePill → PublicNavbar so visitors get the same top nav as /pilot, /wall-of-tenants, etc.
import PublicNavbar from '../components/PublicNavbar';
import AlignKickoffInline from '../components/AlignKickoffInline';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  implemented: { icon: ShieldCheck, cls: 'text-emerald-700', label: 'Implemented' },
  'in-progress': { icon: CircleDot, cls: 'text-amber-600', label: 'In progress' },
  planned: { icon: Clock, cls: 'text-emerald-600/80', label: 'Planned' },
};

const CATEGORY_META = {
  CC: { label: 'Common Criteria', icon: Lock, color: 'from-emerald-500/20 to-teal-500/20' },
  C:  { label: 'Confidentiality', icon: ShieldCheck, color: 'from-emerald-500/20 to-teal-500/20' },
  PI: { label: 'Processing Integrity', icon: Activity, color: 'from-emerald-500/20 to-teal-500/20' },
  A:  { label: 'Availability', icon: Server, color: 'from-amber-500/20 to-orange-500/20' },
};

export default function SOC2Readiness() {
  const [matrix, setMatrix] = useState(null);
  const [summary, setSummary] = useState(null);
  const [artifacts, setArtifacts] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/soc2-readiness/matrix`).then(r => r.json()),
      fetch(`${API}/api/soc2-readiness/summary`).then(r => r.json()),
      fetch(`${API}/api/soc2-readiness/artifacts`).then(r => r.json()),
    ]).then(([m, s, a]) => { setMatrix(m); setSummary(s); setArtifacts(a); });
  }, []);

  if (!matrix || !summary) {
    return (
      <div className="dark min-h-screen bg-white text-slate-900">
        <div className="max-w-5xl mx-auto px-6 py-20 animate-pulse">
          <div className="h-12 bg-slate-200 rounded mb-6" />
          <div className="h-64 bg-slate-200 rounded" />
        </div>
      </div>
    );
  }

  // Group controls by category
  const byCategory = matrix.controls.reduce((acc, c) => {
    (acc[c.category] = acc[c.category] || []).push(c);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="soc2-readiness-page">
      <SEOHead title="SOC 2 Type II Readiness | CARTIEAI" description="Live control matrix tracking CARTIE's progress toward SOC 2 Type II certification (target: September 30, 2026). 100% of controls implemented." ogSlug="soc2-readiness" />
      <PublicNavbar />

      {/* Hero */}
      <section className="px-6 pt-24 pb-12 max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <Badge className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 mb-4 text-[11px] font-mono uppercase tracking-widest">
            <ShieldCheck className="w-3 h-3 mr-1.5 inline" />
            Live Control Matrix · Updated Jul 4, 2026
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-5 tracking-tight text-slate-900">
            SOC 2 Type II <span className="text-emerald-600">readiness</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            We don't have the Type II cert yet — audit targeted <span className="text-slate-900 font-semibold">September 30, 2026</span> with A-LIGN. Here's <span className="text-slate-900">every Trust Services Criteria control</span> with our current implementation status. No vague "compliance overview" — the actual matrix.
          </p>
        </div>

        {/* Readiness score */}
        <div className="rounded-2xl bg-slate-900 text-slate-100 border border-emerald-500/30 p-8 mb-8 text-center shadow-md shadow-emerald-500/10" data-testid="soc2-readiness-card">
          <div className="text-[10px] font-bold tracking-widest text-emerald-300 uppercase mb-3">Overall readiness</div>
          <div className="text-7xl font-bold text-emerald-600 tabular-nums" data-testid="soc2-readiness-pct">{summary.overall_readiness_pct}%</div>
          <div className="text-sm text-slate-400 mt-3">
            {summary.controls_total} controls across {Object.keys(summary.by_category).length} TSC categories · audit target <span className="text-white font-semibold">{summary.audit_target_date}</span>
          </div>
        </div>

        {/* Category breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="soc2-category-summary">
          {Object.entries(summary.by_category).map(([key, val]) => {
            const meta = CATEGORY_META[key] || { label: key, icon: ShieldCheck, color: '' };
            const Icon = meta.icon;
            const pct = val.total ? Math.round((val.implemented / val.total) * 100) : 0;
            return (
              <div key={key} className="rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold tracking-widest text-emerald-600 uppercase">{key}</span>
                  <Icon className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="text-2xl font-bold text-slate-900 tabular-nums">{pct}%</div>
                <div className="text-[11px] text-slate-400">{meta.label}</div>
                <div className="text-[10px] text-slate-500 mt-1.5">{val.implemented}/{val.total} done</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Control matrix */}
      <section className="px-6 pb-12 max-w-5xl mx-auto space-y-8">
        <AlignKickoffInline />
        {Object.entries(byCategory).map(([cat, controls]) => {
          const meta = CATEGORY_META[cat] || { label: cat, icon: ShieldCheck };
          const Icon = meta.icon;
          return (
            <div key={cat} className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm" data-testid={`soc2-category-${cat}`}>
              <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center gap-3">
                <div className="inline-flex w-9 h-9 rounded-lg bg-emerald-500 bg-gradient-to-br from-emerald-500 to-teal-500 items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-white drop-shadow-sm" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">{meta.label}</h2>
                  <p className="text-xs text-slate-500">{controls.length} control{controls.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-200">
                  {controls.map((c, i) => {
                    const s = STATUS[c.status] || STATUS.planned;
                    const SIcon = s.icon;
                    return (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-6 py-3 align-top">
                          <div className="flex items-start gap-3">
                            <SIcon className={`w-4 h-4 ${s.cls} shrink-0 mt-0.5`} />
                            <div className="flex-1">
                              <div className="font-medium text-slate-900">{c.control}</div>
                              <div className="text-xs text-slate-400 mt-0.5 max-w-xl">{c.description}</div>
                              {c.target && c.status !== 'implemented' && (
                                <div className="text-[11px] text-emerald-700 mt-1">Target: {c.target}</div>
                              )}
                              {c.evidence && (
                                c.evidence.startsWith('/') ? (
                                  <Link to={c.evidence} className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-800 mt-1">
                                    Evidence <ExternalLink className="w-2.5 h-2.5" />
                                  </Link>
                                ) : (
                                  <div className="text-[11px] text-slate-500 font-mono mt-1">{c.evidence}</div>
                                )
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3 text-right align-top">
                          <Badge className={`${s.cls} bg-slate-100 border border-slate-200 text-[10px] font-bold uppercase tracking-widest`}>
                            {s.label}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </section>

      {/* Evidence artifacts */}
      {artifacts && (
        <section className="px-6 pb-12 max-w-5xl mx-auto">
          <div className="rounded-2xl bg-slate-900 text-slate-100 border border-white/10 p-6">
            <h3 className="text-lg font-bold mb-3 text-white flex items-center gap-2"><FileCheck className="w-4 h-4 text-emerald-400" /> Public evidence artifacts</h3>
            <p className="text-sm text-slate-400 mb-4">Your procurement team can verify each artifact in 1 click — no contact-sales walls.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(artifacts).map(([k, v]) => (
                <Link key={k} to={v} className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-slate-900 border border-white/10 hover:border-emerald-500/40 transition-colors text-sm">
                  <span className="text-slate-300 capitalize">{k.replace(/_/g, ' ')}</span>
                  <span className="text-emerald-300 font-mono text-xs">{v} <ExternalLink className="inline w-3 h-3" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Forward navigation — clear next-step CTAs (Feb 24 2026) */}
      <section className="px-6 pb-20 max-w-5xl mx-auto" data-testid="soc2-next-steps">
        <div className="rounded-2xl bg-slate-900 text-slate-100 border border-emerald-500/30 p-8 shadow-md shadow-emerald-500/10">
          <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Procurement-ready. Audit-ready.</h3>
          <p className="text-slate-300 mt-2 mb-6 max-w-2xl">
            Need to validate vendor security before a pilot? Take 14 days, no commitment. We&apos;ll provide direct artifact access.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            <Link to="/pricing">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 h-12 w-full sm:w-auto shadow-md shadow-emerald-500/20 transition-all hover:-translate-y-0.5" data-testid="soc2-cta-pilot">
                Start free pilot
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/honest-scorecard">
              <Button size="lg" variant="outline" className="border-emerald-400/40 bg-emerald-500/5 text-emerald-200 hover:bg-emerald-500/15 hover:text-white px-6 h-12 w-full sm:w-auto" data-testid="soc2-cta-scorecard">
                Honest Scorecard
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="border-white/10 bg-transparent text-slate-400 hover:bg-white/5 hover:text-white px-6 h-12 w-full sm:w-auto" data-testid="soc2-cta-home">
                ← Back to home
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
